import { Col, Icon, Row, Spinner2, Switch, Text } from 'components';
import { Api, POLL_STATE, POLL_TYPE, getPollGameLabel, theme } from 'lib';
import { format } from 'date-fns';
import React, { useState } from 'react';
import styled from 'styled-components';

const Chip = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-family: CeraPro;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  background: ${(props) => props.bg};
  color: ${(props) => props.fg};
`;

// Icon-only button. The clickable area matches the visible icon (no extra padding).
const IconBtn = styled.button`
  background: none;
  border: none;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  border-radius: 6px;
  padding: 0;
  color: ${(props) => (props.danger ? theme.SECONDARY : theme.GREY_60)};
  transition: background 0.12s ease;
  &:hover {
    background: ${theme.GREY_10};
  }
`;

const TYPE_CHIP = {
  [POLL_TYPE.GAME_DAY]: { label: 'Game day', bg: `${theme.PURPLE}20`, fg: theme.PURPLE },
  [POLL_TYPE.NON_GAME_DAY]: { label: 'Non-game day', bg: `${theme.GREEN}20`, fg: theme.GREEN }
};

const STATE_CHIP = {
  [POLL_STATE.OPEN]: { label: 'Open', bg: `${theme.GREEN}20`, fg: theme.GREEN },
  [POLL_STATE.CLOSED]: { label: 'Closed', bg: `${theme.GREY_20}`, fg: theme.GREY_70 },
  [POLL_STATE.HIDDEN]: { label: 'Hidden', bg: `${theme.ORANGE}20`, fg: theme.ORANGE }
};

// 12-hour clock throughout — admins read these at a glance and 24-hour reads as a code.
const DATE_TIME = 'MMM dd, h:mm a';

const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

// When the poll runs. Game-day polls open and close off the match clock, so they show the
// match rather than a start/end pair.
const formatWindow = (p) => {
  if (p.pollType === POLL_TYPE.GAME_DAY) {
    const label = p.game && typeof p.game === 'object' ? getPollGameLabel(p.game) : '';
    return `${label || 'Match TBD'} · timing automatic`;
  }
  if (p.startDate && p.endDate) {
    return `${format(new Date(p.startDate), DATE_TIME)} → ${format(
      new Date(p.endDate),
      DATE_TIME
    )}`;
  }
  return 'No schedule set';
};

const PollListTable = ({
  polls,
  loading,
  isP4P,
  // Rows come from more than one channel under "All channels", so name the owner.
  showChannel,
  onEdit,
  onViewResults,
  onViewParticipants,
  onDelete,
  onToggleActive
}) => {
  const [togglingId, setTogglingId] = useState(null);

  const handleToggle = async (p, e) => {
    e?.stopPropagation();
    setTogglingId(p._id);
    const res = await Api.put(
      '/admin/polls/update',
      { id: p._id, isActive: !p.isActive },
      null,
      { silent: true }
    );
    setTogglingId(null);
    if (res?.data || res?.msg) {
      onToggleActive(res.data || { ...p, isActive: !p.isActive });
    }
  };

  if (loading) {
    return (
      <Row centerAll pad="40px 0">
        <Spinner2 />
      </Row>
    );
  }

  if (!polls.length) {
    return (
      <Row centerAll pad="40px 0">
        <Text col={theme.GREY_60}>No polls match the current filters.</Text>
      </Row>
    );
  }

  return (
    <Col wid="100%" gap="8px">
      {polls.map((p) => {
        const type = TYPE_CHIP[p.pollType] || TYPE_CHIP[POLL_TYPE.NON_GAME_DAY];
        const state = STATE_CHIP[p.state] || STATE_CHIP[POLL_STATE.HIDDEN];
        const isAuto = p.source === 'auto';
        // Team admins can't edit/delete/toggle a poll a P4P admin created (mirrors backend).
        const manageable = isP4P || p.createdByRole !== 'p4p';
        const votes = p.totalSubmissions || 0;
        return (
          <Row
            key={p._id}
            between
            pad="14px 18px"
            style={{
              background: 'white',
              borderRadius: 10,
              border: `1px solid ${theme.GREY_20}`,
              alignItems: 'center'
            }}
          >
            {/* LEFT: question on top, then each meta item on its own line below */}
            <Col
              flex={1}
              style={{ minWidth: 0, alignItems: 'flex-start', alignSelf: 'flex-start' }}
            >
              <Row noFlex gap="8px" style={{ minWidth: 0, width: '100%', alignItems: 'flex-start' }}>
                <Text
                  med
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflowWrap: 'anywhere'
                  }}
                >
                  {p.question}
                </Text>
                {isAuto && (
                  <Chip bg={`${theme.TALE}25`} fg={theme.GREY_70} title="Generated from the lineup">
                    Auto
                  </Chip>
                )}
              </Row>
              {showChannel && p.team && typeof p.team === 'object' && (
                <Text c2 col={theme.PRIMARY} marg="4px 0 0">
                  {p.team.name || p.team.username}
                </Text>
              )}

              {/* When it runs */}
              <Text c2 col={theme.GREY_60} marg="6px 0 0">
                {formatWindow(p)}
              </Text>

              {/* How it performed — emphasized once anyone has voted, so a live poll
                  stands out from an untouched one at a glance. */}
              <Text
                c2
                med={votes > 0}
                col={votes > 0 ? theme.GREY_80 : theme.GREY_40}
                marg="4px 0 0"
              >
                {plural(votes, 'vote', 'votes')}
              </Text>

              {/* Configuration, deliberately quietest */}
              <Text c2 col={theme.GREY_40} marg="4px 0 0">
                {plural(p.rewardPoints || 0, 'pt', 'pts')} ·{' '}
                {plural(p.options?.length || 0, 'option', 'options')}
                {p.createdAt ? ` · created ${format(new Date(p.createdAt), 'MMM dd, yyyy')}` : ''}
              </Text>
            </Col>

            {/* RIGHT: type + state chips, active toggle, actions */}
            <Row noFlex center gap="12px" marg="0 0 0 16px" style={{ alignSelf: 'center' }}>
              <Chip bg={type.bg} fg={type.fg}>
                {type.label}
              </Chip>
              <Chip bg={state.bg} fg={state.fg}>
                {state.label}
              </Chip>

              <Row noFlex center>
                <Text c2 col={p.isActive ? theme.GREEN : theme.GREY_60} marg="0 6px 0 0">
                  {p.isActive ? 'Active' : 'Inactive'}
                </Text>
                {manageable && (
                  <Switch
                    activated={p.isActive}
                    onClick={(e) => handleToggle(p, e)}
                    disabled={togglingId === p._id}
                  />
                )}
              </Row>

              <IconBtn onClick={() => onViewResults(p)} title="View results" type="button">
                <Icon type="barChart" col="currentColor" size={16} />
              </IconBtn>

              <IconBtn
                onClick={() => onViewParticipants(p)}
                title="View participants"
                type="button"
              >
                <Icon type="people" col="currentColor" size={16} />
              </IconBtn>

              {manageable ? (
                <>
                  <IconBtn onClick={() => onEdit(p)} title="Edit" type="button">
                    <Icon type="edit" col="currentColor" size={16} />
                  </IconBtn>

                  <IconBtn onClick={() => onDelete(p)} title="Delete" type="button" danger>
                    <Icon type="delete" col="currentColor" size={16} />
                  </IconBtn>
                </>
              ) : (
                <Text c2 col={theme.GREY_40} title="Created by a P4P admin">
                  Managed by P4P
                </Text>
              )}
            </Row>
          </Row>
        );
      })}
    </Col>
  );
};

export default PollListTable;
